import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';
import { Link } from 'react-router-dom';

const EquipmentSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const equipment = [
    {
      title: 'Flatbeds',
      description: 'Heavy-duty flatbed trucks for transporting construction materials and equipment',
      image: 'https://public.readdy.ai/ai/img_res/9a24159ff340ed15f403b21ceb05ff1e.jpg',
      link: '/equipment/flatbed',
      icon: 'ri-truck-line',
    },
    {
      title: 'Manlift',
      description: 'Aerial work platforms for safe elevated access to construction sites',
      image: 'https://readdy.ai/api/search-image?query=modern%20telescopic%20boom%20lift%20manlift%20equipment%20at%20construction%20site%2C%20professional%20aerial%20work%20platform%20photography%2C%20worker%20in%20safety%20gear%20operating%20elevated%20platform%2C%20clean%20industrial%20background%2C%20bright%20daylight%20professional%20photography%2C%20side%20angle%20showing%20full%20extension%20capability&width=600&height=400&seq=manlift-001&orientation=landscape',
      link: '/equipment/manlift',
      icon: 'ri-arrow-up-down-line',
    },
    {
      title: 'Forklift',
      description: 'Industrial forklifts for efficient material handling and warehouse operations',
      image: 'https://readdy.ai/api/search-image?query=industrial%20forklift%20truck%20lifting%20pallets%20at%20construction%20warehouse%2C%20professional%20material%20handling%20equipment%20photography%2C%20modern%20forklift%20with%20operator%20in%20safety%20gear%2C%20clean%20simple%20background%2C%20bright%20daylight%2C%20three%20quarter%20angle%20showing%20lifting%20mechanism%20and%20stability&width=600&height=400&seq=forklift-001&orientation=landscape',
      link: '/equipment/forklift',
      icon: 'ri-stack-line',
    },
    {
      title: 'Scissor Lift',
      description: 'Stable vertical platforms for indoor and outdoor elevated work',
      image: 'https://readdy.ai/api/search-image?query=electric%20scissor%20lift%20platform%20fully%20extended%20at%20construction%20site%2C%20professional%20aerial%20work%20platform%20photography%2C%20worker%20on%20elevated%20platform%2C%20clean%20industrial%20background%2C%20bright%20daylight%20professional%20photography%2C%20side%20angle%20showing%20scissor%20mechanism%20and%20platform%20stability&width=600&height=400&seq=scissor-lift-001&orientation=landscape',
      link: '/equipment/scissor-lift',
      icon: 'ri-layout-grid-line',
    },
  ];

  return (
    <section id="equipment" className="py-24 bg-neutral-50">
      <div className="container mx-auto px-4 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-accent rounded-full"></div>
            <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Additional Services</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
            Other Equipment
          </h2>
          <p className="text-neutral-500 font-inter text-lg max-w-2xl mx-auto">
            Complete your project with our comprehensive range of construction equipment
          </p>
        </motion.div>

        {/* Equipment Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {equipment.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={item.link}
                className="group block bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Icon */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-poppins font-bold text-primary mb-3 group-hover:text-accent transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-neutral-600 font-inter text-sm leading-relaxed mb-4">
                    {item.description}
                  </p>
                  
                  <div className="flex items-center gap-2 text-accent font-inter font-semibold text-sm">
                    Learn More
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EquipmentSection;
